import deepEqual from 'fast-deep-equal';
import type { ActorSystem, AnyActorRef } from 'xstate';
import { EMPTY_ADDRESS_FORM } from '~/constants';
import { sendSuccessAlert } from '~/features/utils';
import { ALERT_VIEW_STATE, PROVIDER_SERVICE, VAULT_STATE } from '~/features/xstateSystem';
import type { Provider } from '~/types';
import { getData } from '~/utils/service';
import { EMPTY_EMAIL_FORM, EMPTY_PHONE_FORM } from './constants';
import {
  OnDeleteAddress,
  OnDeleteEmailAddress,
  OnDeletePhoneNumber,
  OnEditAddress,
  OnEditEmailAddress,
  OnEditPhoneNumber,
  OnLoad,
  OnSetActiveTab,
  OnSubmitAddress,
  OnSubmitEmailAddresses,
  OnSubmitPhoneNumbers,
  ProviderResponseEvent,
} from './events';
import { Context, Scope } from './types';
import {
  addressesProvider,
  emailAddressesProvider,
  personalInfoFromProvider,
  phoneNumbersProvider,
  professionalInfoFromProvider,
  sensitiveInfoFromProvider,
} from './utils';

// Util Actions
export const setProvider = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as OnLoad;
  return {
    provider: payload.provider,
  };
};
export const setLoadContext = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as OnLoad;
  return {
    provider: payload.provider,
    personalInfo: personalInfoFromProvider(payload.provider),
    professionalInfo: professionalInfoFromProvider(payload.provider),
    sensitiveInfo: sensitiveInfoFromProvider(payload.provider),
    addresses: addressesProvider(payload.provider),
    emailAddresses: emailAddressesProvider(payload.provider),
    phoneNumbers: phoneNumbersProvider(payload.provider),
  };
};

export const setUpdateContext = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as ProviderResponseEvent;
  const provider = output.data;
  switch (scope.context.submittingTab) {
    case 0:
      return { personalInfo: personalInfoFromProvider(provider) };
    case 1:
      return { sensitiveInfo: sensitiveInfoFromProvider(provider) };
    case 2:
      return { professionalInfo: professionalInfoFromProvider(provider) };
    case 3:
      return {
        addresses: addressesProvider(provider),
        emailAddresses: emailAddressesProvider(provider),
        phoneNumbers: phoneNumbersProvider(provider),
      };
    default:
      return {};
  }
};

export const setActiveTab = (scope: Scope): Partial<Context> => {
  const { tab } = scope.event as OnSetActiveTab;
  return {
    currentTab: tab,
  };
};

export const openAddressModal = (): Partial<Context> => {
  return {
    addressForm: EMPTY_ADDRESS_FORM,
    modal: {
      type: 'address',
      edit: false,
      create: true,
    },
  };
};

export const resetModal = (scope: Scope): Partial<Context> => {
  return {
    modal: {
      ...scope.context.modal,
      edit: false,
      create: false,
    },
  };
};

export const setCurrentTab = (scope: Scope): Partial<Context> => {
  const { tab } = scope.event as OnSetActiveTab;
  return {
    currentTab: tab,
  };
};

// Edit Context actions

export const setEditAddressContext = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnEditAddress;
  return {
    addressForm: scope.context.addresses[index],
    modal: { edit: true, create: false, type: 'address' },
    editAddressIndex: index,
  };
};
export const setEditPhoneNumberContext = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnEditPhoneNumber;
  return {
    modal: {
      type: 'phone',
      edit: true,
      create: false,
    },
    phoneNumberForm: scope.context.phoneNumbers[index],
    editPhoneNumberIndex: index,
  };
};
export const setEditEmailAddressContext = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnEditEmailAddress;
  return {
    modal: {
      type: 'email',
      edit: true,
      create: false,
    },
    emailAddressForm: scope.context.emailAddresses[index],
    editEmailAddressIndex: index,
  };
};

// Submit Context actions

export const setSubmitAddressContext = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as OnSubmitAddress;
  return {
    addresses: values,
    modal: {
      type: 'address',
      edit: false,
      create: false,
    },
  };
};

export const setSubmitPhoneNumbersContext = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as OnSubmitPhoneNumbers;
  return {
    modal: {
      type: 'phone',
      edit: false,
      create: false,
    },
    phoneNumbers: values,
  };
};

export const setSubmitEmailAddressContext = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as OnSubmitEmailAddresses;
  return {
    emailAddresses: values,
  };
};

// Add Context actions
export const setAddPhoneNumberContext = (): Partial<Context> => {
  return {
    modal: {
      type: 'phone',
      edit: false,
      create: true,
    },
    phoneNumberForm: EMPTY_PHONE_FORM,
  };
};

export const setAddEmailContext = (): Partial<Context> => {
  return {
    modal: {
      type: 'email',
      edit: false,
      create: true,
    },
    emailAddressForm: EMPTY_EMAIL_FORM,
  };
};

// Delete actions
export const deleteEmail = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnDeleteEmailAddress;
  return {
    emailAddresses: scope.context.emailAddresses.filter((_, i) => i !== index),
  };
};

export const deletePhone = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnDeletePhoneNumber;
  return {
    phoneNumbers: scope.context.phoneNumbers.filter((_, i) => i !== index),
  };
};

export const deleteAddress = (scope: Scope): Partial<Context> => {
  const { index } = scope.event as OnDeleteAddress;
  return {
    addresses: scope.context.addresses.filter((_, i) => i !== index),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sendProfileSavedAlert = ({ system }: { system: ActorSystem<any> }) => {
  system?.get(ALERT_VIEW_STATE)?.send(
    sendSuccessAlert({
      text: 'Profile Information Saved',
      subText: 'Your profile information has been successfully updated.',
    })
  );
};

export const notifyVaultProviderUpdated = async ({
  system,
  event,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  system: ActorSystem<any>;
  event: ProviderResponseEvent;
}) => {
  const optimistic = event.output?.data;
  if (!optimistic) return;

  // The vault and service are resolved NOW, before the await: a reply that arrives
  // after the session changed must not be delivered to whichever vault is current
  // then. Reference identity (not only the provider id) also rejects a late reply
  // from an earlier session of the same account.
  const vault = system?.get(VAULT_STATE);
  const service = system?.get(PROVIDER_SERVICE);
  const stillCurrent = () =>
    !!vault &&
    vault.getSnapshot().status === 'active' &&
    system?.get(VAULT_STATE) === vault &&
    system?.get(PROVIDER_SERVICE) === service;

  // Fast paint: broadcast the PATCH response immediately so fields the user just edited
  // (category/type selections, names) land in the sidebar without waiting for a round-trip.
  vault?.send({ type: 'PROVIDER_UPDATED', provider: optimistic });

  // MCV-1184: the PATCH response can omit hydrated relations (notably provider.type.providerTypeCode
  // and provider.type.description, which the sidebar "Doctor of …" label reads). Follow up with a
  // fresh GET so the sidebar reflects the saved practitioner type without a page refresh. Also keep
  // providerService in sync for any downstream consumer that reads through it.
  try {
    const hydrated = await getData<Provider>('provider', {
      pathParams: { provider_id: String(optimistic.id) },
    });
    if (hydrated && stillCurrent()) {
      vault?.send({ type: 'PROVIDER_UPDATED', provider: hydrated });
    }
  } catch (e) {
    // Non-fatal — optimistic update already applied.
    console.debug('Provider hydration refresh after update failed:', e);
  }
  if (stillCurrent()) service?.send({ type: 'REFRESH' });
};

// The newest refresh asked of each vault. A response that is not the newest has nothing to add.
const newestRefresh = new WeakMap<AnyActorRef, symbol>();

/**
 * [MCV-1753] Re-read the provider for the vault when a notification click lands on /vault/profile:
 * the profile page replays the provider the vault loaded at boot, so a delegate's edit would stay
 * invisible until a reload. Only a changed server copy is broadcast, so an unchanged profile does
 * not reset the form the user may already be typing in.
 *
 * The response is dropped when it can no longer be trusted: a save (`notifyVaultProviderUpdated`)
 * or another refresh replaced the vault's provider while this request was in flight, or a newer
 * refresh was asked for. Applying it would put the older server copy over the newer one.
 */
export const refreshVaultProvider = async ({
  providerId,
  vault,
}: {
  providerId?: number;
  vault: AnyActorRef;
}) => {
  if (!providerId) return;
  const request = Symbol('refreshVaultProvider');
  newestRefresh.set(vault, request);
  const requestedFrom = vault.getSnapshot().context.provider;
  try {
    const fresh = await getData<Provider>('provider', {
      pathParams: { provider_id: String(providerId) },
    });
    if (newestRefresh.get(vault) !== request) return;
    const current = vault.getSnapshot().context.provider;
    if (current !== requestedFrom) return;
    if (!fresh || deepEqual(fresh, current)) return;
    vault.send({ type: 'PROVIDER_UPDATED', provider: fresh });
  } catch (e) {
    // Non-fatal: the page keeps showing the provider it already has.
    console.debug('[MCV-1753] provider refresh failed', e);
  }
};
